import React, { useReducer, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog, DialogContent, Grid, RadioGroup,
    FormControlLabel, Radio, Checkbox, Button,
} from '@material-ui/core';
import { Form, Col } from 'react-bootstrap';
import clsx from 'clsx';
import update from 'immutability-helper';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import Select from 'components/widgets/Select';
import InputNumber from 'components/widgets/InputNumber';
import { isValidField } from 'utils/schema/utils';
import ButtonStyles from 'styles/theme/Button';
import contractToFormData from './contractUtils';

const useStyles = makeStyles((theme) => ({
    ...ButtonStyles.getStyle(theme),
    dialog: {
        width: 870,
    },
    content: {
        padding: theme.spacing(2),
    },
    formGroup: {
        marginBottom: theme.spacing(1),
    },
    label: {
        fontSize: '14px',
        marginBottom: '2px',
    },
    input: {
        fontSize: '14px',
    },
    inputControl: {
        height: 30,
        padding: '4px 8px',
    },
    checkboxWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.5),
    },
    rateRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1),
        '& .MuiCheckbox-root': {
            padding: 0,
        },
    },
    inlineFieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
    },
    inlineFieldInput: {
        flex: 2,
        minWidth: 0,
    },
    inlineFieldSelect: {
        flex: 3,
        minWidth: 0,
    },
    radioGroup: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: theme.spacing(0.25),
        '& .MuiFormControlLabel-root': {
            margin: 0,
        },
        '& .MuiRadio-root': {
            paddingTop: theme.spacing(0.25),
            paddingBottom: theme.spacing(0.25),
            paddingLeft: 0,
            paddingRight: theme.spacing(0.5),
        },
    },
}));

const yesNoOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
];

const lateFeeCalculatedByOptions = [
    { value: 'Payment', label: 'Payment' },
    { value: 'Past Due', label: 'Past Due' },
];

const formatYesNo = (value) => (value ? 'Yes' : 'No');

const NUMERIC_FIELDS = [
    'lateFeeMin',
    'lateFeeMax',
    'lateFeeDays',
    'lateFeePercentage',
    'nSFCharges',
];

const ACTION_TYPE = {
    SET_LOCAL_FORM_DATA: 'setLocalFormData',
    UPDATE_FIELD: 'updateField',
    SET_ORIGINAL_VALUES: 'setOriginalValues',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.SET_LOCAL_FORM_DATA:
        return update(state, {
            localFormData: { $set: action.value },
        });
    case ACTION_TYPE.UPDATE_FIELD:
        return update(state, {
            localFormData: {
                [action.field]: { $set: action.value },
            },
        });
    case ACTION_TYPE.SET_ORIGINAL_VALUES:
        return update(state, {
            originalValues: { $set: action.value },
        });
    default:
        return state;
    }
};

const ContractDetailDialog = ({
    open, onClose, contract, onClone, onEdit, onEditFieldChange, onSave, onCancel, formData, isEditing, isLoadingDealsCount,
}) => {
    const classes = useStyles();
    const [dialogState, dispatch] = useReducer(reducer, {
        localFormData: {},
        originalValues: {},
    });

    const getOriginalValues = (contractObj) => ({
        lateFeeMin: contractObj.lateFeeMin,
        lateFeeMax: contractObj.lateFeeMax,
        lateFeeDays: contractObj.lateFeeDays,
        lateFeePercentage: contractObj.lateFeePercentage,
        nSFCharges: contractObj.nSFCharges,
    });

    useEffect(() => {
        if (formData) {
            dispatch({
                type: ACTION_TYPE.SET_LOCAL_FORM_DATA,
                value: formData,
            });
        } else if (contract) {
            dispatch({
                type: ACTION_TYPE.SET_LOCAL_FORM_DATA,
                value: contractToFormData(contract),
            });
            // Save original values when dialog opens
            dispatch({
                type: ACTION_TYPE.SET_ORIGINAL_VALUES,
                value: getOriginalValues(contract),
            });
        }
    }, [formData, contract]);

    // Save original values when entering edit mode
    useEffect(() => {
        if (isEditing && contract) {
            dispatch({
                type: ACTION_TYPE.SET_ORIGINAL_VALUES,
                value: getOriginalValues(contract),
            });
        }
    }, [isEditing, contract]);

    const validationErrors = useMemo(() => {
        const errors = [];

        if (isEditing) {
            if (!dialogState.localFormData.name || dialogState.localFormData.name.trim() === '') {
                errors.push({ field: 'name' });
            }

            // Validate numeric fields
            NUMERIC_FIELDS.forEach((field) => {
                const value = dialogState.localFormData[field];
                const originalValue = dialogState.originalValues[field];

                // A numeric field is considered invalid if:
                // 1. It's explicitly null, undefined, or empty string
                // 2. It's 0 but the original value was not 0 (meaning the user cleared the field
                //    and the InputNumber component defaulted it to 0, which we treat as invalid)
                if (
                    value === null
                    || value === undefined
                    || value === ''
                    || (value === 0 && originalValue !== null && originalValue !== undefined && originalValue !== 0)
                ) {
                    errors.push({ field });
                }
            });
        }

        return errors;
    }, [dialogState.localFormData, dialogState.originalValues, isEditing]);

    const isValid = validationErrors.length === 0;

    if (!contract) {
        return null;
    }

    const {
        state,
        isPremium,
        salesTaxDeferred,
        accrualMethod,
        payoffAccrualMethod,
        maxSchedules,
        amortization,
        compounding,
        compoundingDays,
        allowChanges,
        allowNSFChanges,
        allowLateFeesChanges,
        maturityRateActive,
        maturityRate,
        allowLenderVSI,
        allowPrepaidFinanceCharge,
        compilanceLevel,
        disclosureMethod,
    } = contract;

    const handleFieldChange = (field, value) => {
        dispatch({
            type: ACTION_TYPE.UPDATE_FIELD,
            field,
            value,
        });

        const updated = {
            ...dialogState.localFormData,
            [field]: value,
        };

        if (onEditFieldChange && typeof onEditFieldChange === 'function') {
            onEditFieldChange(updated);
        }
    };

    const handleClose = () => {
        if (isEditing && onCancel) {
            onCancel();
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                className: classes.dialog,
            }}
            disableEnforceFocus
        >
            <DialogAppBar
                title="Contract Detail"
                onClose={handleClose}
                iconSize="sm"
                secondaryButton={(
                    <Button
                        size="small"
                        variant="contained"
                        className={classes.containedSecondaryInfo}
                        onClick={onClone}
                        disabled={!onClone || isEditing}
                    >
                        Clone Contract
                    </Button>
                )}
            />
            <DialogContent className={classes.content}>
                <Grid container spacing={1} className="am-form">
                    <Grid item xs={12}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Contract Name</Form.Label>
                            <Form.Control
                                className={clsx(
                                    classes.input,
                                    classes.inputControl,
                                    isValidField(validationErrors, 'name') ? 'invalid-field' : '',
                                )}
                                type="text"
                                value={dialogState.localFormData.name || ''}
                                readOnly={!isEditing}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>State</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={state || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Premium Contract</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(isPremium)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Sales Tax Deferred</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(salesTaxDeferred)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Accrual Method</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={accrualMethod || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Payoff Accrual Method</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={payoffAccrualMethod || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Max # of Schedules</Form.Label>
                            <InputNumber
                                value={maxSchedules || 0}
                                decimalScale={0}
                                size="sm"
                                disabled
                                className={classes.inputControl}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Calc Amortization</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={amortization || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Calc Compounding</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={compounding || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Calc Days in Year</Form.Label>
                            <InputNumber
                                value={compoundingDays || 0}
                                decimalScale={0}
                                size="sm"
                                disabled
                                className={classes.inputControl}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow Contract Changes</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(allowChanges)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow NSF Changes</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(allowNSFChanges)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow Late Fee Changes</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(allowLateFeesChanges)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <div className={classes.checkboxWrapper}>
                                <Form.Label className={classes.label}>Post Maturity Rate</Form.Label>
                                <div className={classes.rateRow}>
                                    <Checkbox
                                        checked={maturityRateActive || false}
                                        disabled
                                        color="primary"
                                    />
                                    <InputNumber
                                        value={maturityRate ?? 0}
                                        decimalScale={2}
                                        size="sm"
                                        disabled
                                        className={classes.inputControl}
                                    />
                                </div>
                            </div>
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow Lender VSI</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(allowLenderVSI)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow Prepaid Finance Charge</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={formatYesNo(allowPrepaidFinanceCharge)}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow Late Fees</Form.Label>
                            <Select
                                name="allowLateFees"
                                value={dialogState.localFormData.allowLateFees}
                                options={yesNoOptions}
                                placeholder="Select"
                                className="select-bootstrap"
                                maxMenuHeight={140}
                                disabled={!isEditing}
                                onChange={handleFieldChange}
                                size="sm"
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Late Fee Minimum</Form.Label>
                            <InputNumber
                                value={dialogState.localFormData.lateFeeMin || 0}
                                decimalScale={2}
                                size="sm"
                                disabled={!isEditing}
                                onChange={(newValue) => handleFieldChange('lateFeeMin', newValue)}
                                className={clsx(
                                    classes.inputControl,
                                    isValidField(validationErrors, 'lateFeeMin') ? 'invalid-field' : '',
                                )}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Late Fee Maximum</Form.Label>
                            <InputNumber
                                value={dialogState.localFormData.lateFeeMax || 0}
                                decimalScale={2}
                                size="sm"
                                disabled={!isEditing}
                                onChange={(newValue) => handleFieldChange('lateFeeMax', newValue)}
                                className={clsx(
                                    classes.inputControl,
                                    isValidField(validationErrors, 'lateFeeMax') ? 'invalid-field' : '',
                                )}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Late Fee Days</Form.Label>
                            <InputNumber
                                value={dialogState.localFormData.lateFeeDays || 0}
                                decimalScale={0}
                                size="sm"
                                disabled={!isEditing}
                                onChange={(newValue) => handleFieldChange('lateFeeDays', newValue)}
                                className={clsx(
                                    classes.inputControl,
                                    isValidField(validationErrors, 'lateFeeDays') ? 'invalid-field' : '',
                                )}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Late Fee Percentage</Form.Label>
                            <div className={classes.inlineFieldRow}>
                                <InputNumber
                                    value={dialogState.localFormData.lateFeePercentage || 0}
                                    decimalScale={2}
                                    size="sm"
                                    disabled={!isEditing}
                                    onChange={(newValue) => handleFieldChange('lateFeePercentage', newValue)}
                                    className={clsx(
                                        classes.inputControl,
                                        classes.inlineFieldInput,
                                        isValidField(validationErrors, 'lateFeePercentage') ? 'invalid-field' : '',
                                    )}
                                />
                                <Select
                                    name="lateFeeCalculatedBy"
                                    value={dialogState.localFormData.lateFeeCalculatedBy || 'Payment'}
                                    options={lateFeeCalculatedByOptions}
                                    placeholder="Select"
                                    className={`select-bootstrap ${classes.inlineFieldSelect}`}
                                    maxMenuHeight={140}
                                    disabled={!isEditing}
                                    onChange={handleFieldChange}
                                    size="sm"
                                />
                            </div>
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Recommended Compliance Level</Form.Label>
                            <InputNumber
                                value={compilanceLevel || 0}
                                decimalScale={0}
                                size="sm"
                                disabled
                                className={classes.inputControl}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Allow NSF Charge</Form.Label>
                            <Select
                                name="allowNSFCharges"
                                value={dialogState.localFormData.allowNSFCharges}
                                options={yesNoOptions}
                                placeholder="Select"
                                className="select-bootstrap"
                                maxMenuHeight={140}
                                disabled={!isEditing}
                                onChange={handleFieldChange}
                                size="sm"
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>NSF Charge</Form.Label>
                            <InputNumber
                                value={dialogState.localFormData.nSFCharges || 0}
                                decimalScale={2}
                                size="sm"
                                disabled={!isEditing}
                                onChange={(newValue) => handleFieldChange('nSFCharges', newValue)}
                                className={clsx(
                                    classes.inputControl,
                                    isValidField(validationErrors, 'nSFCharges') ? 'invalid-field' : '',
                                )}
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Disclosure Method</Form.Label>
                            <Form.Control
                                className={`${classes.input} ${classes.inputControl}`}
                                type="text"
                                value={disclosureMethod || ''}
                                readOnly
                            />
                        </Form.Group>
                    </Grid>

                    <Grid item xs={12}>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <RadioGroup
                                className={classes.radioGroup}
                                value={dialogState.localFormData.lateFeeAllocationLogic || ''}
                                onChange={(e) => handleFieldChange('lateFeeAllocationLogic', e.target.value)}
                            >
                                <FormControlLabel
                                    value="AfterPaymentDue"
                                    control={<Radio disabled={!isEditing} />}
                                    label="Take Late Fees after payments due are satisfied."
                                />
                                <FormControlLabel
                                    value="EachInstallment"
                                    control={<Radio disabled={!isEditing} />}
                                    label="Take Late Fees due each installment."
                                />
                            </RadioGroup>
                        </Form.Group>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                onClickSecondary={isEditing ? onCancel : handleClose}
                titleSecondary={isEditing ? 'Cancel' : 'Close'}
                onClickPrimary={isEditing ? onSave : onEdit}
                titlePrimary={isEditing ? 'Save' : 'Edit'}
                hiddenPrimaryButton={false}
                disablePrimaryButton={isEditing ? (isLoadingDealsCount || !isValid) : false}
                useAdditionalButton={false}
            />
        </Dialog>
    );
};

ContractDetailDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onClone: PropTypes.func,
    onEdit: PropTypes.func,
    onEditFieldChange: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    formData: PropTypes.object,
    isEditing: PropTypes.bool,
    isLoadingDealsCount: PropTypes.bool,
    contract: PropTypes.shape({
        loanContractTemplateId: PropTypes.number,
        name: PropTypes.string,
        state: PropTypes.string,
        isPremium: PropTypes.bool,
        salesTaxDeferred: PropTypes.bool,
        accrualMethod: PropTypes.string,
        payoffAccrualMethod: PropTypes.string,
        maxSchedules: PropTypes.number,
        amortization: PropTypes.string,
        compounding: PropTypes.string,
        compoundingDays: PropTypes.number,
        allowChanges: PropTypes.bool,
        allowNSFChanges: PropTypes.bool,
        allowLateFeesChanges: PropTypes.bool,
        maturityRateActive: PropTypes.bool,
        maturityRate: PropTypes.number,
        allowLenderVSI: PropTypes.bool,
        allowPrepaidFinanceCharge: PropTypes.bool,
        allowLateFees: PropTypes.bool,
        lateFeeMin: PropTypes.number,
        lateFeeMax: PropTypes.number,
        lateFeeDays: PropTypes.number,
        lateFeePercentage: PropTypes.number,
        lateFeeCalculatedBy: PropTypes.string,
        compilanceLevel: PropTypes.number,
        allowNSFCharges: PropTypes.bool,
        nSFCharges: PropTypes.number,
        disclosureMethod: PropTypes.string,
        lateFeeAllocationLogic: PropTypes.string,
    }),
};

ContractDetailDialog.defaultProps = {
    contract: null,
    onClone: null,
    onEdit: null,
    onEditFieldChange: null,
    onSave: null,
    onCancel: null,
    formData: null,
    isEditing: false,
    isLoadingDealsCount: false,
};

export default ContractDetailDialog;
